import { type ChartTheme, chartZoomDrag, readableInkOn } from '../theme';

import { axisScaleId, CHART_GRID_DASH_PATTERN } from './constants';
import type {
    MetricAnnotationOptions,
    MetricLegendOptions,
    MetricZoomOptions,
} from './optionTypes';
import type { MetricChartKind, MetricChartSpec } from './types';

/**
 * The `options.plugins` entries of a metric chart other than the tooltip: legend,
 * annotations, zoom and the in-bar labels. Split out of `buildChartOptions` to keep that file
 * about axes and tooltips.
 */

const REFERENCE_LINE_WIDTH = 1.5;

/** The reference and vertical lines as chartjs-plugin-annotation entries. */
export function buildAnnotations(
    spec: Pick<MetricChartSpec<MetricChartKind>, 'referenceLines' | 'verticalLines' | 'days'>,
    theme: ChartTheme
): MetricAnnotationOptions {
    const horizontal = (spec.referenceLines ?? []).map((line) => [
        line.key,
        {
            type: 'line' as const,
            yScaleID: axisScaleId(line.axis),
            yMin: line.value,
            yMax: line.value,
            borderColor: theme.color(line.color),
            borderWidth: REFERENCE_LINE_WIDTH,
            ...(line.dashed === false ? {} : { borderDash: CHART_GRID_DASH_PATTERN }),
            ...(line.label
                ? {
                      label: {
                          display: true,
                          content: line.label,
                          position: 'end' as const,
                          backgroundColor: 'transparent',
                          color: theme.color(line.color),
                          // Bold by default, which a single-weight BoxedRound face cannot show.
                          font: { family: theme.strongFontFamily, weight: 'normal' as const },
                      },
                  }
                : {}),
        },
    ]);
    const vertical = (spec.verticalLines ?? [])
        .filter((line) => spec.days.includes(line.at))
        .map((line) => [
            line.key,
            {
                type: 'line' as const,
                xScaleID: 'x',
                xMin: spec.days.indexOf(line.at),
                xMax: spec.days.indexOf(line.at),
                borderColor: theme.color(line.color),
                borderWidth: line.width ?? REFERENCE_LINE_WIDTH,
                ...(line.dashed === false ? {} : { borderDash: CHART_GRID_DASH_PATTERN }),
                ...(line.label
                    ? {
                          label: {
                              display: true,
                              content: line.label,
                              position: 'center' as const,
                              backgroundColor: theme.color(line.color),
                              // Bold by default, which a single-weight BoxedRound face cannot show.
                              font: { family: theme.strongFontFamily, weight: 'normal' as const },
                              xAdjust: line.labelOffset?.x ?? 0,
                              yAdjust: line.labelOffset?.y ?? 0,
                          },
                      }
                    : {}),
            },
        ]);
    const entries = [...horizontal, ...vertical];
    return entries.length > 0 ? { annotation: { annotations: Object.fromEntries(entries) } } : {};
}

/** The HTML legend is the default; a `'canvas'` spec gets the Chart.js legend on top. */
export function buildLegendOptions(
    spec: Pick<MetricChartSpec, 'legend' | 'legendAlign' | 'legendBoxSize'>,
    theme: ChartTheme
): MetricLegendOptions {
    if (spec.legend !== 'canvas') return { display: false };
    const box = spec.legendBoxSize;
    return {
        display: true,
        position: 'top' as const,
        ...(spec.legendAlign ? { align: spec.legendAlign } : {}),
        labels: {
            color: theme.text,
            ...(box === undefined ? {} : { boxWidth: box, boxHeight: box }),
        },
    };
}

/** Pan on x and drag or pinch to zoom, unless the spec turns zoom off. */
export function buildZoomOptions(
    spec: Pick<MetricChartSpec, 'zoom'>,
    theme: ChartTheme
): MetricZoomOptions {
    if (spec.zoom === false) return {};
    return {
        zoom: {
            pan: {
                enabled: true,
                mode: 'x' as const,
            },
            zoom: {
                wheel: { enabled: false },
                pinch: { enabled: true },
                drag: {
                    enabled: true,
                    ...chartZoomDrag(theme),
                    borderWidth: 1,
                },
                mode: 'x' as const,
            },
        },
    };
}

/** One bar series' labels and the ink each is drawn in, read by `metricBarLabelsPlugin`. */
export interface MetricBarLabelSet {
    /** Index of the dataset the labels belong to. */
    datasetIndex: number;
    labels: (string | null)[];
    /** Ink for a label inside its bar, per bar: readable on that bar's fill. */
    insideInk: string[];
}

export interface MetricBarLabelsOptions {
    sets: MetricBarLabelSet[];
    /** Ink for a label drawn above its bar, on the chart surface. */
    aboveInk: string;
}

/** Labels for every bar series that has them, with their inks resolved for `theme`. */
export function buildBarLabelOptions(
    spec: Pick<MetricChartSpec<MetricChartKind>, 'series' | 'days' | 'kind'>,
    theme: ChartTheme
): { metricBarLabels: MetricBarLabelsOptions } | Record<string, never> {
    const sets = spec.series.flatMap((series, datasetIndex) => {
        const isBar = (series.type ?? spec.kind) === 'bar';
        if (!isBar || !series.barLabels) return [];
        const insideInk = spec.days.map((_day, index) =>
            readableInkOn(
                theme.color(series.barColors?.[index] ?? series.color),
                theme.barInk.dark,
                theme.barInk.light
            )
        );
        return [{ datasetIndex, labels: series.barLabels, insideInk }];
    });
    return sets.length > 0 ? { metricBarLabels: { sets, aboveInk: theme.labelInk } } : {};
}
